import {
  Building2,
  CalendarDays,
  CheckCircle,
  Clock,
  CreditCard,
  Crown,
  DollarSign,
  Eye,
  Hash,
  Image as ImageIcon,
  Lightbulb,
  Mail,
  Mic,
  MoreHorizontal,
  Percent,
  Phone,
  Tag,
  Ticket,
  TriangleAlert,
  User,
  UserRound,
  UserRoundCheck,
  type LucideIcon,
} from "lucide-react";

/**
 * The icon that belongs beside a table column's name.
 *
 * Keyed on the column label rather than on a per-table list, because the same
 * columns recur across the workspace — Actions, Date, Status, Registered and
 * Amount each appear in three or more tables — and a per-table list means the
 * events table and the payments table eventually disagree about what a date looks
 * like. One mapping is what keeps them the same.
 *
 * Order is significant: the first pattern that matches wins, so a narrower label
 * has to be listed before the general one it contains. "Subscription date" is a
 * column of dates, so it matches the date rule before the subscription rule.
 */
const COLUMN_ICONS: ReadonlyArray<{ match: RegExp; Icon: LucideIcon }> = [
  { match: /featured image/i, Icon: ImageIcon },
  { match: /portrait|photo/i, Icon: UserRound },
  { match: /speaker/i, Icon: Mic },
  { match: /proposed topic|topic/i, Icon: Lightbulb },
  { match: /business use case|use case/i, Icon: Ticket },
  { match: /preview/i, Icon: Eye },
  { match: /email/i, Icon: Mail },
  { match: /contact/i, Icon: Phone },
  { match: /company/i, Icon: Building2 },
  { match: /name/i, Icon: User },
  { match: /registered/i, Icon: UserRoundCheck },
  { match: /time/i, Icon: Clock },
  { match: /date|renews on|attempted|created|requested/i, Icon: CalendarDays },
  { match: /subscription/i, Icon: CreditCard },
  { match: /discount/i, Icon: Percent },
  { match: /amount|collected/i, Icon: DollarSign },
  { match: /failure/i, Icon: TriangleAlert },
  { match: /reference/i, Icon: Hash },
  { match: /status|decision/i, Icon: CheckCircle },
  { match: /type/i, Icon: Tag },
  { match: /member/i, Icon: Crown },
  { match: /event/i, Icon: Ticket },
  { match: /actions/i, Icon: MoreHorizontal },
];

export function crmColumnIcon(label: string): LucideIcon | null {
  const trimmed = label.trim();
  if (!trimmed) return null;
  return COLUMN_ICONS.find(entry => entry.match.test(trimmed))?.Icon ?? null;
}

/**
 * A column name with its icon.
 *
 * The icon takes no colour of its own — Lucide draws with `currentColor` — so it
 * is whatever the header cell already is. Each table's header keeps its own
 * colour, and no table needs to repeat it here to stay in step.
 *
 * `aria-hidden`, because the icon says nothing the label does not: a screen reader
 * announcing "calendar Date" is worse than "Date".
 */
export default function CrmColumnHeading({
  label,
  icon,
  size = 14,
}: {
  label: string;
  /** For a label whose meaning differs by table — "Member" is a tier in the roster and a person in payments. */
  icon?: LucideIcon | null;
  size?: number;
}) {
  const Icon = icon === undefined ? crmColumnIcon(label) : icon;
  if (!Icon) return <>{label}</>;

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: ".35rem", whiteSpace: "nowrap" }}>
      <Icon size={size} strokeWidth={2} aria-hidden="true" style={{ flex: "0 0 auto" }} />
      {label}
    </span>
  );
}
